import { Box, Button, Grid, IconButton, Paper, Snackbar, Tab, Tabs, Typography } from '@mui/material';
import { Add, ContentCopy, OpenInNew } from '@mui/icons-material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import RotatingLoader from '../common/ui/RotatingLoader';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { StorefrontData, StorefrontStatus } from '../api';
import { StorefrontCard } from './components/StorefrontCard';
import { StorefrontDrawer } from './components/StorefrontDrawer';
import { mb } from '../common/theme/mbTokens';
import { accountSellsGiftCards, getGiftCardPageLink } from '../utils/AccountUtils';

export const StorefrontsOverviewPage = () => {
  const { t } = useTranslation();
  const { account, isLoading, updateLocalAccount } = useGetAccountAuthenticated({});
  const passesEnabled = useFeatureFlagEnabled('feature-passes');
  const [activeTab, setActiveTab] = useState<StorefrontStatus>(StorefrontStatus.ACTIVE);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedStorefront, setSelectedStorefront] = useState<StorefrontData | undefined>(undefined);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Grid container sx={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <RotatingLoader />
      </Grid>
    );
  }

  const storefronts = account?.storefronts || [];
  const activeStorefronts = storefronts.filter((s) => s.status === StorefrontStatus.ACTIVE);
  const archivedStorefronts = storefronts.filter((s) => s.status === StorefrontStatus.ARCHIVED);
  const displayedStorefronts = activeTab === StorefrontStatus.ACTIVE ? activeStorefronts : archivedStorefronts;

  const portalLink = `${import.meta.env.VITE_REACT_APP_APP_DOMAIN}/passes/${account?.id}`;
  const publicLinks = [
    ...activeStorefronts
      .filter((s) => !!s.short_code)
      .map((s) => ({
        key: s.id,
        label: s.name ?? t('storefronts.title'),
        url: `${import.meta.env.VITE_REACT_APP_LINKS_HOST}/${s.short_code}`,
      })),
    ...(passesEnabled ? [{ key: 'portal', label: t('passes.customer_portal'), url: portalLink }] : []),
    ...(account && accountSellsGiftCards(account)
      ? [{ key: 'giftcard', label: t('storefronts.gift_card_page'), url: getGiftCardPageLink(account.id) }]
      : []),
  ];

  const handleCopyLink = (url: string) => {
    void navigator.clipboard.writeText(url);
    setCopiedUrl(url);
  };

  const handleCreateNew = () => {
    setSelectedStorefront(undefined);
    setDrawerOpen(true);
  };

  const handleEdit = (storefront: StorefrontData) => {
    setSelectedStorefront(storefront);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedStorefront(undefined);
  };

  return (
    <Grid
      container
      flexDirection="column"
      flexWrap="nowrap"
      height="100%"
      minWidth={0}
      sx={{ p: mb.layout.cardPadding }}>
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">{t('storefronts.title')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreateNew}>
          {t('storefronts.create_storefront')}
        </Button>
      </Grid>

      {publicLinks.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, alignSelf: 'flex-start' }}>
          <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
            {t('storefronts.public_links')}
          </Typography>
          <Grid container flexDirection={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={1}>
            {publicLinks.map((link) => (
              <Grid
                item
                key={link.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '6px',
                  paddingInlineEnd: 1,
                }}>
                <Box sx={{ display: 'flex' }}>
                  <IconButton size="small" onClick={() => handleCopyLink(link.url)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => window.open(link.url, '_blank')}>
                    <OpenInNew fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {link.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label={`${t('storefronts.active')} (${activeStorefronts.length})`} value={StorefrontStatus.ACTIVE} />
        <Tab label={`${t('storefronts.archived')} (${archivedStorefronts.length})`} value={StorefrontStatus.ARCHIVED} />
      </Tabs>

      {displayedStorefronts.length === 0 && activeTab === StorefrontStatus.ACTIVE ? (
        <Grid container flexDirection="column" alignItems="center" justifyContent="center" gap={2} py={8}>
          <Typography variant="h6" color="text.secondary">
            {t('storefronts.empty_state_title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('storefronts.empty_state_description')}
          </Typography>
          <Button variant="outlined" startIcon={<Add />} onClick={handleCreateNew}>
            {t('storefronts.create_storefront')}
          </Button>
        </Grid>
      ) : displayedStorefronts.length === 0 ? (
        <Grid container justifyContent="center" py={4}>
          <Typography color="text.secondary">{t('storefronts.empty_state_title')}</Typography>
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ overflow: 'auto', flexShrink: 1, minHeight: 0 }}>
          {displayedStorefronts.map((storefront) => (
            <Grid item xs={12} sm={6} md={4} key={storefront.id}>
              <StorefrontCard
                storefront={storefront}
                account={account}
                onEdit={() => handleEdit(storefront)}
                updateLocalAccount={updateLocalAccount}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <StorefrontDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        storefront={selectedStorefront}
        account={account}
        updateLocalAccount={updateLocalAccount}
        existingStorefronts={storefronts}
      />
      <Snackbar
        open={!!copiedUrl}
        autoHideDuration={2000}
        onClose={() => setCopiedUrl(null)}
        message={t('storefronts.link_copied')}
      />
    </Grid>
  );
};
